import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  TextField,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import { Close, Delete, Edit } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { getAllSize } from "../../../User/State/Size/Action";
import { toast } from "react-toastify";
import { createVariant, deleteVariant, updateVariant } from "../../State/ProductVariant/Action";
import { findProductById } from "../../../User/State/Product/Action";
import { getVendorProfile } from "../../State/Vendor/Action";

const VariantModal = ({ open, onClose, row, loading }) => {
  const { products, size, vendorProfile, variants } = useSelector((store) => store);
  const [variantData, setVariantData] = useState({
    colorIds: [],
    sizeIds: [],
    price: "",
    stockQuantity: "",
    vendorDiscountPercentage: "",
    marginPrice: "",
    adminDiscountPercentage: "",
  });
  const [editingId, setEditingId] = useState(null);
  const dispatch = useDispatch();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteVariantId, setDeleteVariantId] = useState(null);
  const [variantList, setVariantList] = useState(products?.product?.variants || []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(getAllSize());
    dispatch(getVendorProfile())
  }, [dispatch]);

  useEffect(() => {
    dispatch(findProductById(row?.id));
    setVariantList(products?.product?.variants || []);
  }, [dispatch, products?.product, row?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Allow only digits in phone
    if (name === "price" && !/^\d*$/.test(value)) return;
    if (name === "stockQuantity" && !/^\d*$/.test(value)) return;
    if (name === "vendorDiscountPercentage" && !/^\d*$/.test(value)) return;
    if (name === "marginPrice" && !/^\d*$/.test(value)) return;
    if (name === "adminDiscountPercentage" && !/^\d*$/.test(value)) return;

    setVariantData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !variantData.colorIds ||
      !variantData.sizeIds ||
      !variantData.price ||
      !variantData.stockQuantity
    ) {
      toast.error("Please fill all the fields");
      return;
    }

    if (variantData.price < 49 || variantData.price > 1000000) {
      toast.error("Variant price should be between 49 to 1000000");
      return;
    }

    if (variantData.stockQuantity < 1) {
      toast.error("Variant stock quantity more then 1 !!");
      return;
    }

    if (editingId) {
      dispatch(updateVariant(editingId, variantData)).then(dispatch(findProductById(row?.id)));
    } else {
      dispatch(createVariant(row?.id, variantData)).then(dispatch(findProductById(row?.id)));
    }
    dispatch(findProductById(row?.id)); // Refresh product data after create/update
    setVariantData({ colorIds: [], sizeIds: [], price: "", stockQuantity: "", vendorDiscountPercentage: "", marginPrice: "", adminDiscountPercentage: "" });
    setEditingId(null);
  };

  const handleVariantDelete = (variantId) => {
    setDeleteVariantId(variantId);
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    dispatch(deleteVariant(deleteVariantId)).then(dispatch(findProductById(row?.id)));
    setConfirmDialogOpen(false);
    setDeleteVariantId(null);
    setVariantData({ colorIds: [], sizeIds: [], price: "", stockQuantity: "", vendorDiscountPercentage: "", marginPrice: "", adminDiscountPercentage: "" });
    setEditingId(null);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
    setDeleteVariantId(null);
  };


  const handleVariantUpdate = (item) => {
    setVariantData({
      colorIds: [item?.colorId] || [],
      sizeIds: [item?.sizeId],
      price: item.price,
      stockQuantity: item.stockQuantity,
      vendorDiscountPercentage: item.vendorDiscountPercentage,
      marginPrice: item.marginPrice,
      adminDiscountPercentage: item.adminDiscountPercentage,
    });
    setEditingId(item.id);
  };

  const handleModalClose = () => {
    setVariantData({
      colorIds: [],
      sizeIds: [],
      price: "",
      stockQuantity: "",
      vendorDiscountPercentage: "",
      marginPrice: "",
      adminDiscountPercentage: "",
    });
    setEditingId(null);
    setVariantList([])
    onClose(); // call parent close
  };


  const rows = variantList?.map((variant) => ({
    id: variant?.id,
    sku: variant?.sku,
    price: variant?.price,
    size: variant?.size?.name,
    color: variant?.productColor?.color?.name || "N/A",
    stockQuantity: variant?.stockQuantity,
    sizeId: variant?.size?.id,
    colorId: variant?.productColor?.id,
    productId: row?.id,
    product: row,
    gstPercentage: row?.igst,
    vendorDiscountPercentage: variant?.vendorDiscountPercentage,
    vendorDiscountPrice: variant?.vendorDiscountPrice?.toFixed(2),
    vendorActualPrice: variant?.vendorActualPrice?.toFixed(2),
    vendorGstPrice: variant?.vendorIGstPrice?.toFixed(2),
    totalPrice: variant?.vendorPrice?.toFixed(2),
    marginPrice: variant?.marginPrice,
    adminPrice: variant?.adminPrice?.toFixed(2),
    payablePrice: variant?.payableAmount,
    adminDiscountPercentage: variant?.adminDiscountPercentage,
    customerPrice: variant?.customerPrice,
  }))
    || [];

  const showActions = !vendorProfile.vendor && row?.productStatus === "APPROVED"; // or from prop

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 60,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "color", headerName: "Color", flex: 1, },
    { field: "size", headerName: "Size", flex: 1, },
    {
      field: "price", headerName: "Price", flex: 1, renderCell: (params) => {
        const value = Number(params.value) || 0;
        return value.toFixed(2);
      }
    },
    { field: "vendorDiscountPrice", headerName: "Discount Amt", flex: 1, },
    { field: "vendorActualPrice", headerName: "Actual Amt", flex: 1, },
    { field: "vendorGstPrice", headerName: "GST Amt", flex: 1, },
    { field: "totalPrice", headerName: "Total Price", flex: 1, },
    ...(showActions ? [
      {
        field: "marginPrice", headerName: "Margin Price", flex: 1, renderCell: (params) => {
          const value = Number(params.value) || 0;
          return value.toFixed(2);
        }
      },
      { field: "adminDiscountPercentage", headerName: "Admin Discount (%)", flex: 1, },
      { field: "adminPrice", headerName: "Admin Price", flex: 1, },
    ] : []),
    { field: "stockQuantity", headerName: "Stock", flex: 1, },
    { field: "sku", headerName: "SKU", flex: 2, },
    {
      field: "actions",
      headerName: "Actions",
      width: 70,
      renderCell: (params) =>
        params?.row ? (
          <Box display="flex" >
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleVariantUpdate(params.row)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                onClick={() => handleVariantDelete(params.row.id)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null,
    }
  ];

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "90%" : "85%",
            maxWidth: 950,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            maxHeight: "100vh",
            overflowY: "auto"
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Add Variant</Typography>
            <IconButton onClick={handleModalClose}>
              <Close />
            </IconButton>
          </Box>

          {/* Readonly Field */}
          <TextField
            label="Product Title"
            value={row?.title}
            name="title"
            size="small"
            fullWidth
            disabled
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />

          {/* Three Fields in a Row */}
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap={2}
            mb={2}
          >
            <FormControl fullWidth size="small">
              <InputLabel id="color-label">Variant Color</InputLabel>
              <Select
                labelId="color-label"
                id="colorIds"
                name="colorIds"
                label="Variant Color"
                value={variantData.colorIds}
                onChange={handleChange}
                multiple
              >
                <MenuItem value={0} disabled>
                  Select Single Or Multiple Product color
                </MenuItem>
                {products?.product?.colors?.map((color) => (
                  <MenuItem key={color.id} value={color.id}>
                    {color?.color?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="size-label">Variant Size</InputLabel>
              <Select
                labelId="size-label"
                id="sizeIds"
                name="sizeIds"
                label="Variant Size"
                value={variantData.sizeIds}
                onChange={handleChange}
                multiple
              >
                <MenuItem value={0} disabled>
                  Select Single Or Multiple Size
                </MenuItem>
                {size.sizes?.response?.map((size) => (
                  <MenuItem key={size.id} value={size.id}>
                    {size.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap={2}
            mb={2}
          >
            <TextField
              label="Stock Quantity"
              name="stockQuantity"
              size="small"
              value={variantData.stockQuantity}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Price"
              name="price"
              size="small"
              value={variantData.price}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Discount (%)"
              name="vendorDiscountPercentage"
              size="small"
              value={variantData.vendorDiscountPercentage}
              onChange={handleChange}
              fullWidth
              inputProps={{ maxLength: 2 }}
            />
          </Box>
          {!vendorProfile.vendor && row?.productStatus === "APPROVED" && (
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              gap={2}
              mb={2}
            >
              <TextField
                label="Margin"
                name="marginPrice"
                size="small"
                value={variantData.marginPrice}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                label="Admin Discount (%)"
                name="adminDiscountPercentage"
                size="small"
                value={variantData.adminDiscountPercentage}
                onChange={handleChange}
                fullWidth
                inputProps={{ maxLength: 2 }}
              />
            </Box>
          )}

          {/* Centered Button */}
          <Box display="flex" justifyContent="center" mb={2}>
            <Button variant="contained" onClick={handleSubmit} sx={{ backgroundColor: "#ef4444", color: "#fff" }}>
              {editingId ? "Update " : "Add "} Variant
            </Button>
          </Box>

          {/* Data Grid */}
          <Box height={270}>
            <DataGrid
              rows={rows}
              columns={columns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 5, page: 0 } },
              }}
              pageSizeOptions={[5, 10, 15, 20]}
              disableRowSelectionOnClick
              loading={variants?.loading || loading}
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  padding: "8px",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Box>
        </Box>

      </Modal>
      <Dialog open={confirmDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Variant?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button sx={{
            "&:hover": {
              color: "white",
            },
          }} onClick={cancelDelete}>Cancel</Button>
          <Button sx={{
            "&:hover": {
              color: "white",
            },
          }} onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VariantModal;
